import React, { useState, useRef, useEffect } from 'react';
import { Info, X } from 'lucide-react';

export default function LetterboxdTutorialPopover() {
  const [isOpen, setIsOpen] = useState(false);
  const popoverRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        isOpen &&
        popoverRef.current &&
        buttonRef.current &&
        !popoverRef.current.contains(event.target as Node) &&
        !buttonRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  // Prevent body scroll when modal is open on mobile
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  return (
    <>
      <button
        ref={buttonRef}
        onClick={() => setIsOpen(!isOpen)}
        className="inline-flex items-center justify-center hover:opacity-70 transition-opacity"
        aria-label="How to export from Letterboxd"
      >
        <Info color="#fdba74" className="size-4 hover:cursor-pointer" />
      </button>

      {isOpen && (
        <>
          {/* Backdrop for mobile */}
          <div className="md:hidden fixed inset-0 bg-black bg-opacity-70 z-40" />
          
          {/* Popover/Modal */}
          <div
            ref={popoverRef}
            className="fixed md:absolute left-0 top-0 md:top-8 z-50 
                       w-full h-full md:h-auto md:w-96 
                       bg-gray-900 rounded-none md:rounded-lg 
                       shadow-2xl border-0 md:border border-gray-700 
                       overflow-hidden flex flex-col"
          >
            <div className="bg-orange-500 text-white px-4 py-4 md:py-3 flex items-center justify-between">
              <h3 className="font-bold text-xl md:text-lg">How to Export from Letterboxd</h3>
              <button
                onClick={() => setIsOpen(false)}
                className="hover:bg-orange-600 rounded p-1 transition-colors"
                aria-label="Close"
              >
                <X className="w-6 h-6 md:w-5 md:h-5" />
              </button>
            </div>

            <div className="p-6 md:p-5 overflow-y-auto flex-1 text-gray-100">
              <ol className="space-y-5 md:space-y-4 text-base md:text-sm">
                <li className="flex gap-3">
                  <span className="flex-shrink-0 w-7 h-7 md:w-6 md:h-6 bg-orange-500 text-white rounded-full flex items-center justify-center font-bold text-sm md:text-xs">
                    1
                  </span>
                  <span className="pt-0.5">Log in to your Letterboxd account</span>
                </li>

                <li className="flex gap-3">
                  <span className="flex-shrink-0 w-7 h-7 md:w-6 md:h-6 bg-orange-500 text-white rounded-full flex items-center justify-center font-bold text-sm md:text-xs">
                    2
                  </span>
                  <div className="pt-0.5">
                    <p className="mb-2">Go to Settings → Data, or visit:</p>
                    <a
                      href="https://letterboxd.com/settings/data/"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-orange-400 hover:text-orange-300 underline font-medium break-all"
                    >
                      letterboxd.com/settings/data/
                    </a>
                  </div>
                </li>

                <li className="flex gap-3">
                  <span className="flex-shrink-0 w-7 h-7 md:w-6 md:h-6 bg-orange-500 text-white rounded-full flex items-center justify-center font-bold text-sm md:text-xs">
                    3
                  </span>
                  <span className="pt-0.5">Click the <strong className="text-white">"Export your data"</strong> button</span>
                </li>

                <li className="flex gap-3">
                  <span className="flex-shrink-0 w-7 h-7 md:w-6 md:h-6 bg-orange-500 text-white rounded-full flex items-center justify-center font-bold text-sm md:text-xs">
                    4
                  </span>
                  <span className="pt-0.5">Extract the downloaded .zip file to a location on your device</span>
                </li>

                <li className="flex gap-3">
                  <span className="flex-shrink-0 w-7 h-7 md:w-6 md:h-6 bg-orange-500 text-white rounded-full flex items-center justify-center font-bold text-sm md:text-xs">
                    5
                  </span>
                  <div className="pt-0.5">
                    <p className="mb-2">Choose which file to upload:</p>
                    <div className="bg-gray-800 border border-gray-700 rounded p-3 space-y-2.5">
                      <div>
                        <span className="font-semibold text-orange-300">Option A:</span> Upload{' '}
                        <code className="bg-gray-700 text-orange-200 px-1.5 py-0.5 rounded text-xs">
                          watched.csv
                        </code>{' '}
                        (at the root) to import <strong className="text-white">all watched movies</strong>
                      </div>
                      <div className="border-t border-gray-600 pt-2">
                        <span className="font-semibold text-orange-300">Option B:</span> Upload{' '}
                        <code className="bg-gray-700 text-orange-200 px-1.5 py-0.5 rounded text-xs">
                          films.csv
                        </code>{' '}
                        (in the "likes" folder) to import <strong className="text-white">only liked movies</strong>
                      </div>
                    </div>
                  </div>
                </li>

                <li className="flex gap-3">
                  <span className="flex-shrink-0 w-7 h-7 md:w-6 md:h-6 bg-orange-500 text-white rounded-full flex items-center justify-center font-bold text-sm md:text-xs">
                    6
                  </span>
                  <span className="pt-0.5">
                    After uploading, you'll see the number of movies ready to import!
                  </span>
                </li>

                <li className="flex gap-3 pb-2">
                  <span className="flex-shrink-0 w-7 h-7 md:w-6 md:h-6 bg-green-600 text-white rounded-full flex items-center justify-center font-bold text-sm md:text-xs">
                    ℹ
                  </span>
                  <span className="pt-0.5 text-gray-300 italic">
                    Don't worry—if you've already imported some movies, they won't be
                    duplicated. We'll only add new ones to your collection.
                  </span>
                </li>
              </ol>
            </div>
          </div>
        </>
      )}
    </>
  );
}